import React, {useRef, useState} from "react";
import {DataTable} from "primereact/datatable";
import {Column} from "primereact/column";
import {Button} from "primereact/button";
import {confirmPopup, ConfirmPopup} from "primereact/confirmpopup";
import {Toast} from "primereact/toast";
import DatasetTitle from "components/basic/display-titles/DatasetTitle";
import {
    IFieldInputProps,
    InputChangeEvent,
} from "components/pages/scheduled-task-airflow/components/InputFields";
import {DatasetDropdown} from "components/basic/dropdowns/dataset/DatasetDropdown";
import {IDatasetInputOutputPair} from "api/apiModels";
import InfoLabel from "../InfoLabel";

interface ITaskConfigDatasetPairTableProps extends IFieldInputProps {
    value?: IDatasetInputOutputPair[] | null
    onChange?: (e: InputChangeEvent<IDatasetInputOutputPair[]>) => void;
}

interface IDatasetPairRowModel {
    value: IDatasetInputOutputPair,
    editValue: IDatasetInputOutputPair,
    editMode: boolean,
}


export default function TaskConfigDatasetPairTable(props: ITaskConfigDatasetPairTableProps) {
    const toast = useRef<Toast>(null);
    const [dataArr, setDataArr] = useState<IDatasetPairRowModel[]>(mapToList(props.value!!));


    const propagateChangesStateUp = (arr: IDatasetPairRowModel[]) => {
        const tmp = { property: props.property,  value: arr.map((e) => e.value!!) } as InputChangeEvent<IDatasetInputOutputPair[]>
        props.onChange!!(tmp);
    }

    const confirmWrapper = (event: any, confirmHandler: any) => {
        confirmPopup({
            target: event.currentTarget,
            message: 'Are you really want to delete this dataset pair mapping?',
            icon: 'pi pi-exclamation-triangle',
            accept: confirmHandler,
        });
    }

    const showWarn = (warn: string) => {
        toast.current?.show({severity:'warn', summary: 'Invalid', detail: warn, life: 3000});
    }

    const handleEdit = (row: IDatasetPairRowModel) => {
        const idx = dataArr.indexOf(row)
        dataArr[idx].editMode = true
        dataArr[idx].editValue = dataArr[idx].value
        setDataArr([...dataArr])
    }

    const handleEditCancel = (row: IDatasetPairRowModel) => {
        const idx = dataArr.indexOf(row)
        // Check if item is selected, if not remove row
        if(row.value?.input == null && row.value?.output == null) {
            const newArr = [...dataArr]
            newArr.splice(idx, 1);
            setDataArr(newArr);
            return;
        }

        dataArr[idx].editMode = false
        dataArr[idx].editValue = dataArr[idx].value
        setDataArr([...dataArr])
    }

    const handleEditConfirm = (row: IDatasetPairRowModel) => {
        const idx = dataArr.indexOf(row)

        if(row.editValue?.input == null || row.editValue?.output == null) {
            showWarn("Datasets must be selected!");
            return;
        }

        dataArr[idx].editMode = false
        dataArr[idx].value = dataArr[idx].editValue
        setDataArr([...dataArr])
        propagateChangesStateUp([...dataArr])
    }

    const handleAdd = () => {
        const updated = [...dataArr, {value: {}, editValue: {},  editMode: true } as IDatasetPairRowModel]
        setDataArr(updated);
    }

    const handleDelete = (row: IDatasetPairRowModel) => {
        const filtered = [...dataArr.filter(item => item.value !== row.value)];
        setDataArr(filtered);
        propagateChangesStateUp(filtered)
    }

    const handleValueChange = (e: any, row: IDatasetPairRowModel, type: keyof IDatasetInputOutputPair) => {
        const idx = dataArr.indexOf(row)
        dataArr[idx].editValue[type] = e
        setDataArr([...dataArr])
    }

    const headerTemplate = (
        <div className="flex justify-content-between">
            <span/>
            <Button icon="pi pi-plus" className="p-button-success" onClick={() => handleAdd()}/>
        </div>
    );

    const inputDatasetBodyTemplate = (row: IDatasetPairRowModel) => datasetBodyTemplate(row, "input")
    const outputDatasetBodyTemplate = (row: IDatasetPairRowModel) => datasetBodyTemplate(row, "output")

    const datasetBodyTemplate = (row: IDatasetPairRowModel, type: keyof IDatasetInputOutputPair) => {
        return <>
            { !row.editMode &&
                <DatasetTitle identifier={row.value[type]!!}/>
            }

            { row.editMode &&
                <DatasetDropdown value={row.editValue[type]!!} onChange={e => handleValueChange(e, row, type) }/>
            }
        </>
    }


    const actionBodyTemplate = (row: IDatasetPairRowModel) => {
        return <>
            { !row.editMode &&
                <Button icon="pi pi-pencil" className="p-button-info p-button-rounded p-button-text" onClick={(e) => handleEdit(row)} />
            }

            { row.editMode &&
                <Button icon="pi pi-check" className="p-button-info p-button-rounded p-button-text" onClick={(e) => handleEditConfirm(row)} />
            }

            { row.editMode &&
                <Button icon="pi pi-times" className="p-button-plain p-button-rounded p-button-text" onClick={(e) => handleEditCancel(row)} />
            }

            <Button icon="pi pi-trash" className="const-del-btn  p-button-rounded p-button-text p-button-danger" onClick={(e) => confirmWrapper(e, () => handleDelete(row))} />
        </>
    }


    return <div className={`field col-${props.width || 12} md:col-${props.widthMd || 12}`}>
        <InfoLabel htmlFor={`${props.property}`} info={props.info}>{props.label}</InfoLabel>

        <DataTable value={dataArr}
                   responsiveLayout="scroll"
                   header={headerTemplate}
                   width={"100%"}
                   size="small"
                   reorderableRows
        >
            <Column rowReorder style={{width: '3em'}} />
            <Column header="Input" body={inputDatasetBodyTemplate}/>
            <Column header="Output" body={outputDatasetBodyTemplate}/>
            <Column body={actionBodyTemplate} bodyStyle={{textAlign: "right", width: "12rem"}}/>
        </DataTable>

        <ConfirmPopup />

        <Toast ref={toast}></Toast>
    </div>
}

function mapToList(map: IDatasetInputOutputPair[] | null): IDatasetPairRowModel[] {
    if(map == null) return []
    return map.map((item) => {
        return {value: item, editValue: item, editMode: false}
    });
}